import VueCookies from "vue-cookies";

/**
 * One Laravel Echo (Reverb) connection per signed-in session.
 *
 * `GetProfile()` runs on boot, after login, and on every workspace switch. It
 * used to call `new Echo()` each time, which opened another websocket and
 * re-registered every listener without closing the old ones — so each switch
 * added a live socket and every realtime event fired N handlers (N toasts,
 * N user-list refetches). Keep the instance here, outside Pinia, so it is
 * created once, not made reactive, and torn down on logout.
 *
 * `laravel-echo` + `pusher-js` are imported lazily so they stay out of the
 * entry bundle (login/public pages never need them).
 */

let echo = null;
let connecting = null;
/** @type {{ channel: string, event: string, handler: Function, leave: boolean }[]} */
let bindings = [];
let boundKey = null;
/** Bumped on disconnect so a connect still in flight is discarded. */
let generation = 0;

function authEndpoint() {
  return import.meta.env.DEV || import.meta.env.VITE_API_SAME_ORIGIN === "true"
    ? "/api/broadcasting/auth"
    : `${import.meta.env.VITE_BASE_API_URL}/broadcasting/auth`;
}

/** Resolve the shared Echo instance, creating it on first call. */
export function connectEcho() {
  if (echo) return Promise.resolve(echo);
  if (connecting) return connecting;

  const gen = generation;
  connecting = Promise.all([import("laravel-echo"), import("pusher-js")])
    .then(([{ default: Echo }, { default: Pusher }]) => {
      window.Pusher = Pusher;
      const scheme = String(import.meta.env.VITE_REVERB_SCHEME || "https")
        .trim()
        .toLowerCase();
      const instance = new Echo({
        broadcaster: "reverb",
        key: import.meta.env.VITE_REVERB_APP_KEY,
        wsHost: import.meta.env.VITE_REVERB_HOST,
        wsPort: 443,
        wssPort: 443,
        forceTLS: scheme === "https",
        enabledTransports: ["ws", "wss"],
        authEndpoint: authEndpoint(),
        auth: {
          headers: {
            Authorization: `Bearer ${VueCookies.get("token")}`,
          },
        },
      });
      if (gen !== generation) {
        instance.disconnect();
        return null;
      }
      echo = instance;
      // Page-level composables (useAuditItemStatusUpdates) read window.Echo.
      window.Echo = echo;
      return echo;
    })
    .finally(() => {
      connecting = null;
    });

  return connecting;
}

function unbindAll() {
  if (!echo) {
    bindings = [];
    return;
  }
  const left = new Set();
  for (const { channel, event, handler, leave } of bindings) {
    echo.private(channel).stopListening(event, handler);
    if (leave && !left.has(channel)) {
      echo.leave(channel);
      left.add(channel);
    }
  }
  bindings = [];
}

/**
 * Replace the app-wide listeners. A repeat call with the same `key` is a
 * no-op, so callers can invoke this freely (every profile reload does).
 *
 * @param {string} key identifies the listener set (e.g. workspace + user)
 * @param {{ channel: string, event: string, handler: Function, leave?: boolean }[]} next
 *   `leave: true` unsubscribes the channel when these listeners are replaced;
 *   omit it for channels page composables also listen on.
 */
export async function bindEchoListeners(key, next) {
  if (key === boundKey) return;
  boundKey = key;

  let instance;
  try {
    instance = await connectEcho();
  } catch {
    // Allow a later profile reload to retry the connection.
    if (key === boundKey) boundKey = null;
    return;
  }
  // A newer call (or disconnect) superseded this one while connecting.
  if (!instance || key !== boundKey || instance !== echo) return;

  unbindAll();
  for (const binding of next) {
    instance.private(binding.channel).listen(binding.event, binding.handler);
    bindings.push({ leave: false, ...binding });
  }
}

/** Close the socket and forget listeners (logout / session expiry). */
export function disconnectEcho() {
  generation += 1;
  boundKey = null;
  unbindAll();
  if (echo) {
    echo.disconnect();
    echo = null;
  }
  if (window.Echo) window.Echo = undefined;
}
